import { useCallback, useEffect, useRef } from 'react';

interface UseInfiniteScrollSentinelInput {
  enabled: boolean;
  loading: boolean;
  onIntersect: () => void;
}

/**
 * Observes a sentinel element and invokes onIntersect when it enters the viewport.
 * UI-only; callers own fetch orchestration (Master Rules §2).
 */
export function useInfiniteScrollSentinel({
  enabled,
  loading,
  onIntersect,
}: UseInfiniteScrollSentinelInput) {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const onIntersectRef = useRef(onIntersect);

  onIntersectRef.current = onIntersect;

  const handleIntersect = useCallback((entries: IntersectionObserverEntry[]) => {
    if (entries[0]?.isIntersecting && enabled && !loading) {
      onIntersectRef.current();
    }
  }, [enabled, loading]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(handleIntersect, { threshold: 0.1 });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [handleIntersect, enabled]);

  return sentinelRef;
}
